// @owners { team: patients-team }
import { SPACING } from '@alto/design-library-tokens';
import { Column, Description, InlineAlert, InputText, MdSpacing, useScreenSize } from '@alto/design-system';
import React, { type ElementRef } from 'react';
import styled from 'styled-components';
import { IMG_ALT_TEXTS } from '~shared/constants';
import { FREEFORM_LABEL_OPTIONAL, LINE_SEPARATED_HEADER } from '~shared/features/rate-shipment/helpers';
import Stars from './Stars';
import { HeaderImage, LARGE_SIZE } from '~web/components/HeaderImage';
import handAndBox from '~web/images/delivery@1x.png';

type Props = {
  readonly rating: number;
  readonly onRatingChange: (rating: number) => void;
  readonly freeformReason: string;
  readonly onFreeformReasonChange: (freeformReason: string) => void;
  readonly errorMessage?: string;
  readonly firstFocusableElementRef?: ElementRef<any>;
  readonly isModal: boolean | null | undefined;
};

const HeaderImageWrapper = styled.div<{ isModal: boolean | null | undefined; isSMScreenOrBigger: boolean }>`
  ${({ isModal }) => `
    padding-top: ${isModal ? SPACING.STATIC.XL.rem : '0'};
  `};

  ${({ isSMScreenOrBigger, isModal }) =>
    !isSMScreenOrBigger &&
    `
      & img {
        display: ${isModal ? 'none' : 'unset'};
      }
  `}
`;

export const RateShipment = ({
  rating,
  onRatingChange,
  freeformReason,
  onFreeformReasonChange,
  errorMessage,
  firstFocusableElementRef,
  isModal,
}: Props) => {
  const onFreeformInputChange = (value: string) => {
    onFreeformReasonChange(value);
  };
  const { isSMScreenOrBigger } = useScreenSize();
  const header = (
    <>
      {LINE_SEPARATED_HEADER[0]}
      <br />
      {LINE_SEPARATED_HEADER[1]}
    </>
  );

  return (
    <Column
      gap="XL"
      centerHorizontally
      flexShrink={0}
    >
      <HeaderImageWrapper
        isSMScreenOrBigger={isSMScreenOrBigger}
        isModal={isModal}
      >
        <HeaderImage
          src={handAndBox}
          alt={IMG_ALT_TEXTS.delivery}
          title={header}
          imgSize={LARGE_SIZE}
        />
      </HeaderImageWrapper>
      <Stars
        rating={rating}
        setRating={onRatingChange}
        isModal={isModal}
        firstFocusableElementRef={firstFocusableElementRef}
      />
      {errorMessage ? (
        <>
          <MdSpacing />
          <InlineAlert type="error">
            <Description>{errorMessage}</Description>
          </InlineAlert>
          <MdSpacing />
        </>
      ) : null}
      <InputText
        autoCapitalize="sentences"
        autoCorrect
        label={FREEFORM_LABEL_OPTIONAL}
        multiline
        numberOfLines={10}
        onChangeText={onFreeformInputChange}
        placeholder="Share additional comments"
        value={freeformReason}
      />
    </Column>
  );
};
